import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { db } from '../utils/firebaseConfig';
import {
  collection,
  query,
  orderBy,
  getDocs,
  where,
  doc,
  getDoc,
} from 'firebase/firestore';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isValid, parseISO, format } from 'date-fns';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { toast } from 'react-toastify';
import { FiClock, FiCalendar, FiFileText, FiBook, FiCheckCircle, FiPackage, FiUser } from 'react-icons/fi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { motion, AnimatePresence } from 'framer-motion';
import defaultAvatar from '../assets/default-avatar.svg';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  COMMON,
  PDF,
  ERRORS,
  BUTTONS,
  STATS,
  FILE_NAMES,
  MODAL_TITLES,
  STATUS,
  CONFIRM,
  PROTOCOL,
  SUCCESS,
  REPORT_TYPES,
} from '../utils/constants';
import { pdfStyles } from '../utils/pdfStyles';
import { parseDate, getBase64ImageFromURL } from '../utils/pdfUtils';
import { getDORPdfContentDefinition, enhancedPdfStyles as dorEnhancedPdfStyles, PDF_CONFIG as DOR_PDF_CONFIG } from './DORDetailPage';
import { getOSRPdfContentDefinition, enhancedPdfStyles as osrEnhancedPdfStyles } from './OfficerSafetyReportDetailPage';
import { getEOWRPdfContentDefinition, preparePerformanceData, enhancedPdfStyles as eowrEnhancedPdfStyles } from './EndOfWeekReportDetailPage';

pdfMake.vfs = pdfFonts.vfs;


const getProgressWidth = (totalHours, requiredHours) =>
  `${requiredHours > 0 ? Math.min((totalHours / requiredHours) * 100, 100) : 0}%`;

const ActionButton = ({ icon: Icon, text, onClick, disabled = false, isLoading = false }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md text-sm font-medium hover:shadow-lg ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-110'}`}
    disabled={disabled || isLoading}
  >
    {isLoading ? (
      <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
    ) : (
      <Icon className="w-4 h-4" />
    )}
    {isLoading ? 'Generating...' : text}
  </button>
);

const DORsPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [traineeDORs, setTraineeDORs] = useState({});
  const [traineeData, setTraineeData] = useState({});
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [compilingTraineeId, setCompilingTraineeId] = useState(null);
  const [activeTabs, setActiveTabs] = useState({}); // Track active tab for each trainee
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Helper function to get/set active tab for a trainee
  const getActiveTab = (traineeId) => activeTabs[traineeId] || 'overview';
  const setActiveTab = (traineeId, tab) => {
    setActiveTabs(prev => ({ ...prev, [traineeId]: tab }));
  };

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiUser },
    { id: 'stats', label: 'Statistics', icon: FiClock },
    { id: 'actions', label: 'Actions', icon: FiFileText }
  ];

  const withPdfLoading = useCallback(async (fn) => {
    try {
      setPdfLoading(true);
      await fn();
    } finally {
      setPdfLoading(false);
    }
  }, []);

  const generatePDF = useCallback(async ({ traineeName, traineeIdNumber }) => {
    await withPdfLoading(async () => {
      // PDF Colors matching DOR style
      const PDF_COLORS = {
        primary: '#0A4DA2',
        secondary: '#3b82f6',
        accent: '#93c5fd',
        text: '#1e293b',
        lightText: '#64748b',
        border: '#cbd5e1',
        headerBg: '#dbeafe',
      };

      let bannerBase64;
      try {
        bannerBase64 = await getBase64ImageFromURL('/banner.png');
      } catch (e) {
        bannerBase64 = null;
      }
      
      let logoBase64;
      try {
        logoBase64 = await getBase64ImageFromURL('/logo.png');
      } catch (e) {
        logoBase64 = null;
      }
      
      const protocolContent = (PROTOCOL.CONTENT || '')
          .split('---')
          .map(section => section.trim())
          .flatMap(section => {
              const lines = section.split('\n').map(line => line.trim());
              if (!lines[0]) return [];
              const title = lines[0];
              const body = lines.slice(1).join('\n').trim();
              const sectionResult = [
                { text: title, fontSize: 14, bold: true, color: PDF_COLORS.primary, margin: [0, 20, 0, 10] }
              ];
              if (body) sectionResult.push({ 
                text: body, 
                fontSize: 10, 
                color: PDF_COLORS.text,
                margin: [0, 5, 0, 15], 
                alignment: 'justify',
                lineHeight: 1.5 
              });
              return sectionResult;
          });

      const content = [];

      // Cover page content - minimalistic design
      content.push(
        // Department name at top
        {
          text: PDF.DEPARTMENT.NAME,
          fontSize: 20,
          bold: true,
          color: PDF_COLORS.primary,
          alignment: 'center',
          margin: [0, 80, 0, 15]
        },
        {
          text: PDF.DEPARTMENT.ADDRESS,
          fontSize: 11,
          color: PDF_COLORS.lightText,
          alignment: 'center',
          margin: [0, 0, 0, 60]
        }
      );

      // Logo if available - smaller and centered
      if (logoBase64) {
        content.push({
          image: logoBase64,
          width: 120,
          height: 120,
          alignment: 'center',
          margin: [0, 0, 0, 60]
        });
      }

      // Main title
      content.push(
        {
          text: PDF.TRAINING_MANUAL.TITLE,
          fontSize: 32,
          bold: true,
          color: PDF_COLORS.primary,
          alignment: 'center',
          margin: [0, 0, 0, 20]
        },
        // Minimalistic trainee info - directly under title
        {
          text: `${traineeName}${traineeIdNumber ? ` • ${traineeIdNumber}` : ''}`,
          fontSize: 16,
          color: PDF_COLORS.text,
          alignment: 'center',
          margin: [0, 0, 0, 140]
        }
      );

      // Footer - more subtle
      content.push({
        text: PDF.TRAINING_MANUAL.SENSITIVE_INFO,
        fontSize: 9,
        italics: true,
        color: PDF_COLORS.lightText,
        alignment: 'center',
        margin: [0, 120, 0, 20]
      });

      // Page break
      content.push({ text: '', pageBreak: 'after' });

      // Protocol content
      content.push(...protocolContent);

      pdfMake.createPdf({
        pageMargins: [40, 20, 40, 40],
        footer: function(currentPage, pageCount) {
          if (currentPage === 1) return null; // No footer on cover page
          return {
            text: `Page ${currentPage} of ${pageCount}`,
            alignment: 'center',
            fontSize: 8,
            margin: [0, 10, 0, 0],
            color: '#6B7280',
          };
        },
        content,
        styles: pdfStyles
      }).download(`${FILE_NAMES.TRAINING_MANUAL(traineeName)}.pdf`);
      toast.success(SUCCESS.PDF_GENERATED);
    });
  }, [withPdfLoading]);

/* ------------------------------------------------------------------
   Consistent "Completion Memo" PDF
   – shares pdfStyles palette + margins
   – cleaner "To / From / Date / Re:" block
   – real horizontal‑rule, not a 1‑row table
-------------------------------------------------------------------*/

const generateMemoPDF = useCallback(
  async ({
    traineeName,
    traineeIdNumber,
    requiredTrainingHours,
    currentUserName,
    currentUserIdNumber,
    earliestDate,
    latestDate,
  }) => {
    await withPdfLoading(async () => {
      // PDF Colors matching DOR style
      const PDF_COLORS = {
        primary: '#0A4DA2',
        secondary: '#3b82f6',
        text: '#1e293b',
        lightText: '#64748b',
        border: '#cbd5e1',
      };

      /* ------------ banner ------------ */
      let banner;
      try {
        banner = await getBase64ImageFromURL('/banner.png');
      } catch { banner = null; }

      /* ------------ dates ------------- */
      const today = format(new Date(), 'MMMM d, yyyy');
      
      const toJsDate = (input) => {
        if (!input) return null;               // nothing
        if (input instanceof Date) return input;
        if (typeof input === 'string') return parseISO(input);      // already ISO text
        if (typeof input === 'object' && input.seconds !== undefined) {
          // Firestore Timestamp → JS Date
          return new Date(input.seconds * 1000);
        }
        return null;                           // unsupported type
      };
      
      const fmt = (input) => {
        const dateObj = toJsDate(input);
        return dateObj && isValid(dateObj) ? format(dateObj, 'MMMM d, yyyy') : 'N/A';
      };

      /* ------------ content ----------- */
      const content = [
        ...(banner ? [{ image: banner, width: 515, alignment: 'center', margin: [0, 0, 0, 12] }] : []),

        /* MEMO TITLE */
        { text: PDF.MEMO.TITLE, style: 'memoTitle', margin: [0, 0, 0, 18] },
        { 
          canvas: [
            {
              type: 'line',
              x1: 0, y1: 0,
              x2: 515, y2: 0,
              lineWidth: 2,
              lineColor: PDF_COLORS.secondary,
            }
          ],
          margin: [0, 0, 0, 18]
        },

        /* HEADER BLOCK (columns keeps the label widths aligned) */
        {
          table: {
            widths: [40, '*'],
            body: [
              ['To:',  { text: PDF.MEMO.TO,                decoration: 'underline' }],
              ['From:',{ text: `${currentUserName} ${currentUserIdNumber}`, decoration: 'underline' }],
              ['Date:',{ text: today,                     decoration: 'underline' }],
              ['Re:',  { text: PDF.MEMO.SUBJECT,          decoration: 'underline' }],
            ],
          },
          layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
          margin: [0, 0, 0, 12],
          style: 'fieldLabel',
        },

        /* fine hair‑line */
        { canvas:[{ type:'line', x1:0, y1:0, x2:515, y2:0, lineWidth:1, lineColor:PDF_COLORS.border }], margin:[0,6,0,16] },

        /* PARAGRAPHS */
        {
          text:
            `Probationary Officer ${traineeName} ${traineeIdNumber} ` +
            `is scheduled to complete the mandatory ${requiredTrainingHours}-hour training phase of the probationary period. ` +
            `Officer ${traineeName} has benefited from the experience and training of the Field Training Program, under the direct supervision of certified Field Training Officers.`,
          style: 'content',
          margin: [0, 0, 0, 14],
        },

        {
          text: earliestDate && latestDate
            ? `The training period commenced on ${fmt(earliestDate)} and concluded on ${fmt(latestDate)}. ` +
              `During this time, Officer ${traineeName} demonstrated dedication and professionalism.`
            : `Officer ${traineeName} ${traineeIdNumber} has completed the training period.`,
          style: 'content',
          margin: [0, 0, 0, 14],
        },

        {
          text:
            `All daily and weekly evaluations, as well as the Training Guide, have been submitted. ` +
            `Therefore, it is respectfully requested that Officer ${traineeName} ${traineeIdNumber} ` +
            `be released from the Field Training Program on ${today}.`,
          style: 'content',
          margin: [0, 0, 0, 26],
        },

        /* MPCTC line */
        {
          text: `${PDF.MEMO.MPCTC_NOTIFICATION}`,
          fontSize: 9,
          margin: [0, 0, 0, 46],
        },

        /* SIGNATURES */
        {
          columns: [
            {
              width: '33%',
              stack: [
                {
                  canvas: [{
                    type: 'line',
                    x1: 0, y1: 0,
                    x2: 150, y2: 0,
                    lineWidth: 1,
                    lineColor: PDF_COLORS.text,
                    dash: { length: 1 },
                  }],
                  margin: [10, 20, 10, 5],
                },
                { text: PDF.MEMO.SIGNATURES.FTO, fontSize: 9, color: PDF_COLORS.lightText, alignment: 'center' },
                { text: `Date: ${today}`, fontSize: 9, color: PDF_COLORS.lightText, alignment: 'center', margin: [0, 5, 0, 0] },
              ]
            },
            {
              width: '34%',
              stack: [
                {
                  canvas: [{
                    type: 'line',
                    x1: 0, y1: 0,
                    x2: 150, y2: 0,
                    lineWidth: 1,
                    lineColor: PDF_COLORS.text,
                    dash: { length: 1 },
                  }],
                  margin: [10, 20, 10, 5],
                  alignment: 'center',
                },
                { text: PDF.MEMO.SIGNATURES.SUPERVISOR, fontSize: 9, color: PDF_COLORS.lightText, alignment: 'center' },
                { text: `Date: ${today}`, fontSize: 9, color: PDF_COLORS.lightText, alignment: 'center', margin: [0, 5, 0, 0] },
              ]
            },
            {
              width: '33%',
              stack: [
                {
                  canvas: [{
                    type: 'line',
                    x1: 0, y1: 0,
                    x2: 150, y2: 0,
                    lineWidth: 1,
                    lineColor: PDF_COLORS.text,
                    dash: { length: 1 },
                  }],
                  margin: [10, 20, 10, 5],
                  alignment: 'right',
                },
                { text: PDF.MEMO.SIGNATURES.CHIEF, fontSize: 9, color: PDF_COLORS.lightText, alignment: 'center' },
                { text: `Date: ${today}`, fontSize: 9, color: PDF_COLORS.lightText, alignment: 'center', margin: [0, 5, 0, 0] },
              ]
            }
          ]
        }
      ];

      /* ------------ make / download --------- */
      pdfMake.createPdf({
        pageMargins: [40, 20, 40, 40],       // matches other reports
        defaultStyle: { fontSize: 10, lineHeight: 1.2 },
        footer: function(currentPage, pageCount) {
          return {
            columns: [
              {
                text: 'Field Training Program - Official Document',
                fontSize: 8,
                color: '#6B7280',
                margin: [40, 10, 0, 0]
              },
              {
                text: `Page ${currentPage} of ${pageCount}`,
                alignment: 'right',
                fontSize: 8,
                color: '#6B7280',
                margin: [0, 10, 40, 0]
              }
            ]
          };
        },
        content,
        styles: {
          ...pdfStyles,
          memoTitle: { fontSize: 24, bold: true, color: PDF_COLORS.primary },
          fieldLabel:{ fontSize: 10, margin:[0,1,0,1], color: PDF_COLORS.text },
        },
      }).download(`${FILE_NAMES.COMPLETION_MEMO(traineeName, traineeIdNumber)}.pdf`);

      toast.success(SUCCESS.MEMO_GENERATED);
    });
  },
  [withPdfLoading],
);


  const generateTraineeReport = useCallback((traineeId) => {
    const traineeInfo = traineeData[traineeId] || { name: COMMON.UNKNOWN, idNumber: COMMON.UNKNOWN };
    return generatePDF({ traineeName: traineeInfo.name, traineeIdNumber: traineeInfo.idNumber });
  }, [traineeData, generatePDF]);

  const generateCompletionMemo = useCallback(async (traineeId) => {
    const traineeInfo = traineeData[traineeId] || { name: COMMON.UNKNOWN, idNumber: COMMON.UNKNOWN, requiredTrainingHours: 0 };
    const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
    const currentUserName = currentUserDoc.exists() ? currentUserDoc.data().name : COMMON.UNKNOWN;
    const currentUserIdNumber = currentUserDoc.exists() ? currentUserDoc.data().idNumber : COMMON.UNKNOWN;

    const dorsQuery = query(collection(db, 'dors'), where('traineeId', '==', traineeId), orderBy('date', 'asc'));
    const dorsSnapshot = await getDocs(dorsQuery);
    const dors = dorsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    const dates = dors.map((dor) => parseDate(dor.date)).filter(isValid).sort((a, b) => a - b);
    const earliestDate = dates[0] || null;
    const latestDate = dates[dates.length - 1] || null;

    return generateMemoPDF({
      traineeName: traineeInfo.name,
      traineeIdNumber: traineeInfo.idNumber,
      requiredTrainingHours: traineeInfo.requiredTrainingHours,
      currentUserName,
      currentUserIdNumber,
      earliestDate,
      latestDate,
    });
  }, [currentUser, traineeData, generateMemoPDF]);

  const fetchAllReportsForTrainee = async (traineeId) => {
    try {
      const dorQuery = query(collection(db, 'dors'), where('traineeId', '==', traineeId), orderBy('date', 'asc'));
      const osrQuery = query(collection(db, 'officerSafetyReports'), where('traineeId', '==', traineeId), orderBy('reportDate', 'asc'));
      const eowrQuery = query(collection(db, 'endOfWeekReports'), where('traineeId', '==', traineeId), orderBy('weekEndingDate', 'asc'));

      const [dorSnapshot, osrSnapshot, eowrSnapshot] = await Promise.all([
        getDocs(dorQuery),
        getDocs(osrQuery),
        getDocs(eowrQuery)
      ]);

      const dors = dorSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data(), reportType: REPORT_TYPES.DOR.SHORT }));
      const osrs = osrSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data(), reportType: REPORT_TYPES.OSR.SHORT }));
      const eowrs = eowrSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data(), reportType: REPORT_TYPES.EOWR.SHORT }));

      return { dors, osrs, eowrs };

    } catch (error) {
      console.error("Error fetching all reports for trainee:", error);
      toast.error(ERRORS.FETCH_FAILED);
      throw error;
    }
  };

  const generateCompiledPDF = async (traineeInfo, reports, traineeId) => {
    try {
      setCompilingTraineeId(traineeId);

      const { dors, osrs, eowrs } = reports;
      const traineeName = traineeInfo.name || COMMON.UNKNOWN;
      const traineeIdNumber = traineeInfo.idNumber || COMMON.UNKNOWN;

      if (!dors.length && !osrs.length && !eowrs.length) {
        toast.error(ERRORS.NO_REPORTS_TO_COMPILE);
        return;
      }

      const compiledContent = [];
      
      // Add cover page for compiled reports matching EOWR style
      try {
        const banner = await getBase64ImageFromURL('/banner.png');
        compiledContent.push({ 
          image: banner, 
          width: DOR_PDF_CONFIG.BANNER_WIDTH, 
          alignment: 'center', 
          margin: [0, 0, 0, 10] 
        });
      } catch (error) {
        console.warn('Failed to load banner image for PDF:', error);
        // Fallback to text header
        compiledContent.push({
          text: 'FIELD TRAINING PROGRAM',
          style: 'bannerText',
          alignment: 'center',
          margin: [0, 0, 0, 10]
        });
      }

      // Report number/identifier at top right
      compiledContent.push({
        text: `Compilation Date: ${format(new Date(), 'MMMM d, yyyy')}`,
        style: 'reportNumber',
        alignment: 'right',
        margin: [0, 0, 0, 5],
      });

      // Title block with enhanced styling (matching EOWR)
      compiledContent.push(
        { text: 'Compiled Training Reports', style: 'header' },
        { 
          canvas: [
            {
              type: 'line',
              x1: 0, y1: 0,
              x2: 515, y2: 0,
              lineWidth: 2,
              lineColor: DOR_PDF_CONFIG.COLORS.secondary,
            }
          ],
          margin: [0, 0, 0, 10]
        },
        { 
          text: `Complete Training Record`, 
          style: 'subheader',
          bold: true,
          fontSize: 12,
          color: DOR_PDF_CONFIG.COLORS.primary,
        },
        {
          table: {
            widths: ['50%', '50%'],
            body: [
              [
                { 
                  text: `Probationary Officer: ${traineeName} ${traineeIdNumber ? `(#${traineeIdNumber})` : ''}`, 
                  style: 'subheader',
                  border: [false, false, false, false],
                },
                { 
                  text: `Total Reports: ${dors.length + osrs.length + eowrs.length}`, 
                  style: 'subheader', 
                  alignment: 'right',
                  border: [false, false, false, false],
                },
              ]
            ]
          },
          layout: 'noBorders',
          margin: [0, 5, 0, 20],
        }
      );

      // Report Information Section with enhanced table (matching EOWR style)
      compiledContent.push({
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: [
                  {
                    table: {
                      widths: ['50%', '50%'],
                      body: [
                        [
                          { 
                            text: `Training Period: ${traineeDORs[traineeId]?.earliestDate ? format(traineeDORs[traineeId].earliestDate, 'MMM d, yyyy') : 'N/A'} - ${traineeDORs[traineeId]?.latestDate ? format(traineeDORs[traineeId].latestDate, 'MMM d, yyyy') : 'N/A'}`, 
                            style: 'infoBox',
                            border: [true, true, false, true],
                            borderColor: [DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border],
                          },
                          { 
                            text: `Total Training Hours: ${traineeDORs[traineeId]?.totalHours || 0} / ${traineeInfo.requiredTrainingHours || 0}`, 
                            style: 'infoBox',
                            border: [false, true, true, true],
                            borderColor: [DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border],
                          },
                        ],
                        [
                          { 
                            text: `Daily Observation Reports: ${dors.length}`, 
                            style: 'infoBox',
                            border: [true, false, false, false],
                            borderColor: [DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border],
                          },
                          { 
                            text: `Officer Safety Reports: ${osrs.length}`, 
                            style: 'infoBox',
                            border: [false, false, true, false],
                            borderColor: [DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border],
                          },
                        ],
                        [
                          { 
                            text: `End of Week Reports: ${eowrs.length}`, 
                            style: 'infoBox',
                            border: [true, false, false, true],
                            borderColor: [DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border],
                          },
                          { 
                            text: `Completion Status: ${traineeDORs[traineeId]?.totalHours >= traineeInfo.requiredTrainingHours ? 'COMPLETE' : 'IN PROGRESS'}`, 
                            style: 'infoBox',
                            fontColor: traineeDORs[traineeId]?.totalHours >= traineeInfo.requiredTrainingHours ? DOR_PDF_CONFIG.COLORS.success : DOR_PDF_CONFIG.COLORS.warning,
                            bold: true,
                            border: [false, false, true, true],
                            borderColor: [DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border, DOR_PDF_CONFIG.COLORS.border],
                          },
                        ]
                      ]
                    },
                    layout: {
                      hLineWidth: (i, node) => 1,
                      vLineWidth: (i, node) => 1,
                      hLineColor: () => DOR_PDF_CONFIG.COLORS.border,
                      vLineColor: () => DOR_PDF_CONFIG.COLORS.border,
                      paddingLeft: () => 0,
                      paddingRight: () => 0,
                      paddingTop: () => 0,
                      paddingBottom: () => 0,
                    }
                  }
                ],
                fillColor: 'white',
                border: [false, false, false, false],
              }
            ]
          ]
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 20],
      });

      // Summary note
      compiledContent.push({
        text: 'This document contains all training reports submitted during the probationary period. Each report maintains its original formatting and content.',
        style: 'footnote',
        margin: [0, 0, 0, 30]
      });

      // Table of Contents
      compiledContent.push(
        { text: 'Table of Contents', style: 'sectionHeader' },
        {
          margin: [0, 0, 0, 8],
          table: {
            widths: ['*'],
            body: [
              [
                {
                  text: 'Click on any section below to jump to that report type',
                  fontSize: 8,
                  italics: true,
                  color: DOR_PDF_CONFIG.COLORS.text,
                  fillColor: DOR_PDF_CONFIG.COLORS.headerBg,
                  alignment: 'center',
                  margin: [10, 4, 10, 4],
                  border: [false, false, false, false],
                }
              ]
            ]
          },
          layout: 'noBorders',
        }
      );

      // TOC entries
      const tocEntries = [];
      if (dors.length > 0) {
        tocEntries.push([
          { text: 'Daily Observation Reports (DORs)', style: 'tableCell', bold: true },
          { text: `${dors.length} reports`, style: 'tableCell', alignment: 'right' }
        ]);
      }
      if (osrs.length > 0) {
        tocEntries.push([
          { text: 'Officer Safety Reports (OSRs)', style: 'tableCell', bold: true },
          { text: `${osrs.length} reports`, style: 'tableCell', alignment: 'right' }
        ]);
      }
      if (eowrs.length > 0) {
        tocEntries.push([
          { text: 'End of Week Reports (EOWRs)', style: 'tableCell', bold: true },
          { text: `${eowrs.length} reports`, style: 'tableCell', alignment: 'right' }
        ]);
      }

      if (tocEntries.length > 0) {
        compiledContent.push({
          table: {
            widths: ['70%', '30%'],
            body: tocEntries
          },
          layout: {
            fillColor: (i) => i % 2 === 0 ? DOR_PDF_CONFIG.COLORS.tableAlt : null,
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingLeft: () => 10,
            paddingRight: () => 10,
            paddingTop: () => 8,
            paddingBottom: () => 8,
          },
          margin: [40, 0, 40, 40]
        });
      }

      // Page break before reports
      compiledContent.push({ text: '', pageBreak: 'after' });

      // Add individual reports
      let isFirstReport = true;
      
      for (const dor of dors) {
        if (!isFirstReport) {
          compiledContent.push({ text: '', pageBreak: 'before' });
        }
        try {
          const dorContent = await getDORPdfContentDefinition(dor);
          compiledContent.push(...dorContent);
          isFirstReport = false;
        } catch (error) {
          console.error(`Error formatting DOR ${dor.id}:`, error);
          toast.error(`Failed to format DOR ${dor.reportId || dor.id}. Skipping.`);
        }
      }

      for (const osr of osrs) {
        if (!isFirstReport) {
          compiledContent.push({ text: '', pageBreak: 'before' });
        }
        try {
          const osrContent = await getOSRPdfContentDefinition(osr);
          compiledContent.push(...osrContent);
          isFirstReport = false;
        } catch (error) {
          console.error(`Error formatting OSR ${osr.id}:`, error);
          toast.error(`Failed to format OSR ${osr.reportId || osr.id}. Skipping.`);
        }
      }

      for (const eowr of eowrs) {
        if (!isFirstReport) {
          compiledContent.push({ text: '', pageBreak: 'before' });
        }
        try {
          const performanceDataForEowr = await preparePerformanceData(eowr, eowr.traineeId);
          const eowrContent = await getEOWRPdfContentDefinition(eowr, performanceDataForEowr);
          compiledContent.push(...eowrContent);
          isFirstReport = false;
        } catch (error) {
          console.error(`Error formatting EOWR ${eowr.id || 'ID unknown'}:`, error);
          toast.error(`Failed to format EOWR ${eowr.reportId || eowr.id || 'ID unknown'}. Skipping.`);
        }
      }

      if (compiledContent.length === 0) {
        toast.error("Failed to format any reports for the PDF.");
        return;
      }

      // Use the exact same PDF configuration and styles as EOWR
      // Create merged styles that include all necessary styles from all report types
      const mergedEnhancedPdfStyles = {
        // Start with base PDF styles
        ...pdfStyles,
        // Add DOR styles
        ...dorEnhancedPdfStyles,
        // Add OSR styles (these are similar to DOR but might have minor differences)
        ...osrEnhancedPdfStyles,
        // Finally, add EOWR styles to ensure table styles are preserved
        ...eowrEnhancedPdfStyles,
        // Explicitly ensure EOWR table styles are not overridden
        tableHeader: {
          fontSize: 9,
          bold: true,
          color: 'white',
          fillColor: '#1e40af', // Blue-800 - matching EOWR
          margin: [4, 2],
        },
        tableHeaderSmall: {
          fontSize: 8,
          color: 'white',
          opacity: 0.9,
          margin: [4, 0],
        },
        tableCell: {
          fontSize: 9,
          color: '#1e293b', // Slate-800
          margin: [4, 2],
        },
        tableCellOne: {
          fontSize: 9,
          bold: true,
          color: '#dc2626', // Red-600
          fillColor: '#fee2e2', // Red-100
          margin: [4, 2],
        },
        tableCellFive: {
          fontSize: 9,
          bold: true,
          color: '#16a34a', // Green-600
          fillColor: '#dcfce7', // Green-100
          margin: [4, 2],
        },
        // Ensure fieldLabel style matches DORDetailPage styling
        fieldLabel: {
          fontSize: 10,
          bold: true,
          color: '#2563eb', // Blue-600 - matching DOR style
          margin: [0, 2, 0, 0],
        },
      };

      const docDefinition = {
        pageMargins: DOR_PDF_CONFIG.PAGE_MARGINS,
        content: compiledContent,
        styles: mergedEnhancedPdfStyles,
        info: {
          title: `Compiled Training Reports - ${traineeName}`,
          author: 'Field Training Management System',
          subject: `All training reports for ${traineeName} (${traineeIdNumber})`,
          keywords: 'field training, compiled reports, DOR, OSR, EOWR, trainee, FTO, probationary officer',
          creator: 'Field Training Management System',
          producer: 'Field Training Management System',
          creationDate: new Date(),
        },
        pageSize: 'LETTER',
        pageOrientation: 'portrait',
        header: (currentPage, pageCount) => {
          // Skip header on first page (cover page)
          if (currentPage === 1) return null;
          
          return {
            columns: [
              {
                text: `Compiled Training Reports - ${traineeName}`,
                style: 'footerText',
                alignment: 'left',
              },
              {
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerText',
                alignment: 'right',
              }
            ],
            margin: [40, 10, 40, 0],
          };
        },
        footer: (currentPage, pageCount) => {
          return {
            text: `Generated on ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")} | Field Training Management System`,
            style: 'footerText',
            alignment: 'center',
            margin: [40, 10, 40, 0],
            fontSize: 7,
            color: DOR_PDF_CONFIG.COLORS.lightText,
          };
        },
      };

      pdfMake.createPdf(docDefinition).download(`${FILE_NAMES.COMPILED_REPORTS(traineeName, traineeIdNumber)}.pdf`);
      toast.success(SUCCESS.COMPILE_REPORTS_GENERATED);

    } catch (error) {
        console.error("Error during PDF generation process:", error);
        toast.error(ERRORS.COMPILE_REPORTS_FAILED);
    } finally {
      setCompilingTraineeId(null);
    }
  };

  const fetchAndGenerateCompiledPDF = async (traineeId) => {
    try {
      const reports = await fetchAllReportsForTrainee(traineeId);
      const traineeInfo = traineeData[traineeId] || { name: COMMON.UNKNOWN, idNumber: COMMON.UNKNOWN };

      if (!reports.dors.length && !reports.osrs.length && !reports.eowrs.length) {
        toast.info(ERRORS.NO_REPORTS_TO_COMPILE);
        setCompilingTraineeId(null);
        return;
      }

      await generateCompiledPDF(traineeInfo, reports, traineeId);
    } catch (error) {
      console.error("Error fetching reports before compiling PDF:", error);
      toast.error(ERRORS.COMPILE_REPORTS_FAILED);
      setCompilingTraineeId(null);
    }
  };

  const handleCompileReports = (traineeId) => {
    const traineeInfo = traineeData[traineeId] || { name: COMMON.UNKNOWN };
    setConfirmModal({
      isOpen: true,
      title: MODAL_TITLES.COMPILE_REPORTS,
      message: CONFIRM.COMPILE_REPORTS(traineeInfo.name),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        await fetchAndGenerateCompiledPDF(traineeId);
      },
    });
    setTimeout(() => modalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  };

  const fetchDORs = useCallback(async () => {    if (!currentUser) {      navigate('/login');      return;    }    try {      setLoading(true);
      const [usersSnapshot, dorsSnapshot, eowrsSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('role', '==', 'trainee'), where('archived', '==', false))),
        getDocs(query(collection(db, 'dors'), orderBy('date', 'desc'))),
        getDocs(query(collection(db, 'endOfWeekReports'), orderBy('weekEndingDate', 'desc'))),
      ]);

      const usersData = {};
      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        usersData[doc.id] = {
          requiredTrainingHours: data.requiredTrainingHours || 0,
          idNumber: data.idNumber || COMMON.UNKNOWN,
          name: data.name || COMMON.UNKNOWN,
          photoURL: data.photoURL || defaultAvatar,
        };
      });

      const dorsItems = dorsSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((dor) => usersData[dor.traineeId]);
      const eowrsItems = eowrsSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((eowr) => usersData[eowr.traineeId]);

      const organizedReports = {};
      Object.keys(usersData).forEach((traineeId) => {
        organizedReports[traineeId] = { dors: [], eowrs: [] };
      });

      dorsItems.forEach((dor) => organizedReports[dor.traineeId]?.dors.push(dor));
      eowrsItems.forEach((eowr) => organizedReports[eowr.traineeId]?.eowrs.push(eowr));

      const summaries = Object.entries(organizedReports).reduce((acc, [traineeId, { dors, eowrs }]) => {
        const totalHours = dors.reduce((sum, dor) => sum + parseInt(dor.shiftLength || 0, 10), 0);
        const dates = [...dors.map((dor) => parseDate(dor.date)), ...eowrs.map((eowr) => parseDate(eowr.weekEndingDate))].filter(isValid);

        if (dates.length === 0) return acc;

        dates.sort((a, b) => a - b);
        const earliestDate = dates[0];
        const latestDate = dates[dates.length - 1];
        const trainingPeriod = earliestDate && latestDate ? Math.max(1, Math.floor((latestDate - earliestDate) / (1000 * 60 * 60 * 24)) + 1) : 0;

        acc[traineeId] = {
          totalHours,
          trainingPeriod,
          earliestDate,
          latestDate,
          daysTrained: dors.length,
          weeksTrained: eowrs.length,
          requiredTrainingHours: usersData[traineeId]?.requiredTrainingHours || 0,
          dors,
          eowrs,
        };
        return acc;
      }, {});

      setTraineeData(usersData);
      setTraineeDORs(summaries);
    } catch (error) {
      console.error('Error fetching DORs and EOWRs:', error);
      toast.error(ERRORS.FETCH_FAILED);
    } finally {
      setLoading(false);
    }
  }, [currentUser, navigate]);  useEffect(() => {    fetchDORs();  }, [fetchDORs]);

  const handlePDFAction = useCallback((action, traineeId) => {
    const REPORT_TYPE = action === 'cover' ? 'cover' : 'memo';
    const traineeInfo = traineeData[traineeId] || { name: COMMON.UNKNOWN };
    setConfirmModal({
      isOpen: true,
      title: REPORT_TYPE === 'cover' ? MODAL_TITLES.GENERATE_COVER : MODAL_TITLES.GENERATE_MEMO,
      message: REPORT_TYPE === 'cover' ? CONFIRM.GENERATE_COVER(traineeInfo.name) : CONFIRM.GENERATE_MEMO(traineeInfo.name),
      onConfirm: async () => {
        await withPdfLoading(() => (REPORT_TYPE === 'cover' ? generateTraineeReport(traineeId) : generateCompletionMemo(traineeId)));
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      },
    });
    setTimeout(() => modalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  }, [generateTraineeReport, generateCompletionMemo, traineeData, withPdfLoading]);

    const traineeSummaries = useMemo(() => Object.entries(traineeDORs), [traineeDORs]);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="flex-1 min-h-[75vh] p-4 pb-24 sm:p-6 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
      >
        <motion.div 
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          className="max-w-5xl mx-auto"
        >
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              />
            ) : traineeSummaries.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, type: "spring" }}
                className="p-6 rounded-xl shadow-lg bg-gradient-to-br from-primary-light to-primary-light/80 dark:from-primary-dark dark:to-primary-dark/80 border border-secondary-light dark:border-secondary-dark flex flex-col items-center text-sm backdrop-blur-sm"
              >
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="text-text-light/90 dark:text-text-dark/90 mb-4"
                >
                  {STATUS.NO_DORS}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                >
                  <ActionButton icon={FiFileText} text={BUTTONS.CREATE_REPORT} onClick={() => navigate('/create-report')} />
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                key="content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-6"
              >
              {traineeSummaries.map(([traineeId, summary], index) => (
                <motion.div
                  key={traineeId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: Math.min(index * 0.1, 0.5),
                    ease: "easeOut"
                  }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="overflow-hidden rounded-xl shadow-lg transition-shadow duration-300 hover:shadow-xl"
                >
                  {/* Colored accent bar for ownership indicator */}
                  <div className="h-1.5 w-full bg-accent-light dark:bg-accent-dark" />
                  
                  {/* Main card content */}
                  <div className="p-6 bg-gradient-to-br from-primary-light to-primary-light/80 dark:from-primary-dark dark:to-primary-dark/80 border border-secondary-light dark:border-secondary-dark backdrop-blur-sm">
                    {/* Header - Always visible */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-12 h-12 rounded-md overflow-hidden border-0 shadow-sm transition-transform duration-300 hover:scale-105 ${!traineeData[traineeId]?.photoURL || traineeData[traineeId]?.photoURL === defaultAvatar ? 'bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark' : ''}`}>
                        {traineeData[traineeId]?.photoURL && traineeData[traineeId]?.photoURL !== defaultAvatar ? (
                          <img src={traineeData[traineeId].photoURL} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-lg font-medium">
                            {traineeData[traineeId]?.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h2 className="text-xl sm:text-2xl font-semibold text-text-light/90 dark:text-text-dark/90 break-words">
                            {traineeData[traineeId]?.name || COMMON.UNKNOWN}
                          </h2>
                          {traineeData[traineeId]?.idNumber && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-accent-light/10 dark:bg-accent-dark/10 text-accent-light dark:text-accent-dark border border-accent-light/20 dark:border-accent-dark/20">
                              #{traineeData[traineeId].idNumber}
                            </span>
                          )}
                        </div>
                        
                        {/* Trainee FTO/Supervisor info would go here if available */}
                        {traineeData[traineeId]?.supervisor && (
                          <div className="flex items-center gap-1 mt-1">
                            <FiUser className="w-3 h-3 text-accent-light dark:text-accent-dark" />
                            <span className="text-xs opacity-70">
                              FTO: {traineeData[traineeId]?.supervisor}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Thicker divider line */}
                    <div className="h-1 bg-secondary-light/90 dark:bg-secondary-dark/90 mb-6 mt-1 rounded-full" />

                    {/* Mobile Tabs - Show on mobile/tablet only */}
                    <div className="block lg:hidden mb-6">
                      <div className="flex space-x-1 bg-secondary-light/20 dark:bg-secondary-dark/20 p-1 rounded-lg">
                        {tabs.map((tab) => (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(traineeId, tab.id)}
                            className={`${
                              getActiveTab(traineeId) === tab.id 
                                ? "text-primary-light dark:text-primary-dark" 
                                : "text-text-light/70 dark:text-text-dark/70 hover:text-text-light dark:hover:text-text-dark"
                            } relative flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 outline-none focus-visible:outline-2 focus-visible:outline-accent-light dark:focus-visible:outline-accent-dark`}
                            style={{
                              WebkitTapHighlightColor: "transparent",
                            }}
                          >
                            {getActiveTab(traineeId) === tab.id && (
                              <motion.span
                                layoutId={`bubble-${traineeId}`}
                                className="absolute inset-0 z-0 bg-accent-light dark:bg-accent-dark"
                                style={{ borderRadius: 6 }}
                                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                              />
                            )}
                            <span className="relative z-10 flex items-center gap-2">
                              <tab.icon className="w-4 h-4" />
                              <span className="hidden sm:inline">{tab.label}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Desktop Layout - Show all content on large screens */}
                    <div className="hidden lg:block">
                      {/* Progress Summary Section */}
                      <div className="mb-6 bg-primary-light/50 dark:bg-primary-dark/50 p-4 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50 shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FiClock className="w-5 h-5 text-accent-light dark:text-accent-dark" />
                            <span className="font-bold text-base">Hours Progress</span>
                          </div>
                          <span className="text-sm font-medium px-2 py-1 rounded-full bg-accent-light/10 dark:bg-accent-dark/10 text-accent-light dark:text-accent-dark">
                            {summary.requiredTrainingHours ? Math.min(((summary.totalHours / summary.requiredTrainingHours) * 100).toFixed(1), 100) : 100}%
                          </span>
                        </div>
                        
                        <div className="flex justify-between mb-1 text-xs text-text-light/80 dark:text-text-dark/80">
                          <span className="font-medium">{summary.totalHours} hours completed</span>
                          <span>{summary.requiredTrainingHours} hours required</span>
                        </div>
                        
                        <div className="w-full rounded-full shadow-inner overflow-hidden h-2.5 bg-secondary-light/60 dark:bg-secondary-dark/60">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              backgroundColor: 'var(--color-accent)',
                              width: getProgressWidth(summary.totalHours, summary.requiredTrainingHours)
                            }}
                          />
                        </div>
                        
                        <div className="text-xs text-text-light/70 dark:text-text-dark/70 mt-2">
                          <span className="italic">
                            {summary.requiredTrainingHours > 0 && summary.totalHours < summary.requiredTrainingHours ? 
                              `${summary.requiredTrainingHours - summary.totalHours} hours remaining` : 
                              'Training hours requirement met'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Stat Cards Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                        {/* Training Period Card */}
                        <div className="bg-primary-light/50 dark:bg-primary-dark/50 p-3 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col">
                          <div className="flex items-center gap-2 mb-1 text-text-light/70 dark:text-text-dark/70">
                            <FiCalendar className="w-4 h-4 text-accent-light dark:text-accent-dark" />
                            <span className="text-xs font-medium uppercase tracking-wide">{STATS.TRAINING_PERIOD}</span>
                          </div>
                          <div className="text-sm">
                            <div className="font-bold text-text-light dark:text-text-dark">
                              {summary.trainingPeriod} days
                            </div>
                            <div className="text-xs mt-1 text-text-light/80 dark:text-text-dark/80">
                              {format(new Date(summary.earliestDate), 'MMM d, yyyy')} - {format(new Date(summary.latestDate), 'MMM d, yyyy')}
                            </div>
                          </div>
                        </div>
                        
                        {/* DORs Written Card */}
                        <div className="bg-primary-light/50 dark:bg-primary-dark/50 p-3 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col">
                          <div className="flex items-center gap-2 mb-1 text-text-light/70 dark:text-text-dark/70">
                            <FiFileText className="w-4 h-4 text-accent-light dark:text-accent-dark" />
                            <span className="text-xs font-medium uppercase tracking-wide">{STATS.DORS_WRITTEN}</span>
                          </div>
                          <div className="font-bold text-sm text-text-light dark:text-text-dark flex items-baseline">
                            {summary.daysTrained}
                            <span className="text-xs ml-1 opacity-70 font-normal">reports</span>
                          </div>
                          {summary.eowrs.length > 0 && (
                            <div className="text-xs mt-1 text-text-light/80 dark:text-text-dark/80">
                              + {summary.eowrs.length} weekly reports
                            </div>
                          )}
                        </div>
                        
                        {/* Total Hours Card */}
                        <div className="bg-primary-light/50 dark:bg-primary-dark/50 p-3 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col">
                          <div className="flex items-center gap-2 mb-1 text-text-light/70 dark:text-text-dark/70">
                            <FiClock className="w-4 h-4 text-accent-light dark:text-accent-dark" />
                            <span className="text-xs font-medium uppercase tracking-wide">{STATS.TOTAL_HOURS}</span>
                          </div>
                          <div className="font-bold text-sm text-text-light dark:text-text-dark flex items-baseline">
                            {summary.totalHours}
                            <span className="text-xs ml-1 opacity-70 font-normal">hrs logged</span>
                          </div>
                          <div className="text-xs mt-1 text-text-light/80 dark:text-text-dark/80">
                            Average: {summary.daysTrained > 0 ? (summary.totalHours / summary.daysTrained).toFixed(1) : 0} hrs per report
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                        <ActionButton icon={FiFileText} text={BUTTONS.VIEW_REPORTS} onClick={() => navigate(`/trainee-reports/${traineeId}`)} disabled={pdfLoading || compilingTraineeId !== null}/>
                        <ActionButton icon={FiBook} text={BUTTONS.COVER_PAGE} onClick={() => handlePDFAction('cover', traineeId)} disabled={pdfLoading || compilingTraineeId !== null} isLoading={pdfLoading} />
                        <ActionButton icon={FiCheckCircle} text={BUTTONS.COMPLETION_MEMO} onClick={() => handlePDFAction('memo', traineeId)} disabled={pdfLoading || compilingTraineeId !== null} isLoading={pdfLoading} />
                        <ActionButton
                          icon={FiPackage}
                          text={BUTTONS.COMPILE_REPORTS}
                          onClick={() => handleCompileReports(traineeId)}
                          isLoading={compilingTraineeId === traineeId}
                          disabled={pdfLoading || (compilingTraineeId !== null && compilingTraineeId !== traineeId)}
                        />
                      </div>
                    </div>

                    {/* Mobile Tabbed Content */}
                    <div className="block lg:hidden">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={getActiveTab(traineeId)}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.2 }}
                        >
                          {/* Overview Tab */}
                          {getActiveTab(traineeId) === 'overview' && (
                            <div className="space-y-4">
                              {/* Progress Summary Section */}
                              <div className="bg-primary-light/50 dark:bg-primary-dark/50 p-4 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50 shadow-sm">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <FiClock className="w-5 h-5 text-accent-light dark:text-accent-dark" />
                                    <span className="font-bold text-base">Hours Progress</span>
                                  </div>
                                  <span className="text-sm font-medium px-2 py-1 rounded-full bg-accent-light/10 dark:bg-accent-dark/10 text-accent-light dark:text-accent-dark">
                                    {summary.requiredTrainingHours ? Math.min(((summary.totalHours / summary.requiredTrainingHours) * 100).toFixed(1), 100) : 100}%
                                  </span>
                                </div>
                                
                                <div className="flex justify-between mb-1 text-xs text-text-light/80 dark:text-text-dark/80">
                                  <span className="font-medium">{summary.totalHours} hours completed</span>
                                  <span>{summary.requiredTrainingHours} hours required</span>
                                </div>
                                
                                <div className="w-full rounded-full shadow-inner overflow-hidden h-2.5 bg-secondary-light/60 dark:bg-secondary-dark/60">
                                  <div
                                    className="h-full rounded-full transition-all duration-1000"
                                    style={{ 
                                      backgroundColor: 'var(--color-accent)',
                                      width: getProgressWidth(summary.totalHours, summary.requiredTrainingHours)
                                    }}
                                  />
                                </div>
                                
                                <div className="text-xs text-text-light/70 dark:text-text-dark/70 mt-2">
                                  <span className="italic">
                                    {summary.requiredTrainingHours > 0 && summary.totalHours < summary.requiredTrainingHours ? 
                                      `${summary.requiredTrainingHours - summary.totalHours} hours remaining` : 
                                      'Training hours requirement met'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Statistics Tab */}
                          {getActiveTab(traineeId) === 'stats' && (
                            <div className="grid grid-cols-1 gap-3">
                              {/* Training Period Card */}
                              <div className="bg-primary-light/50 dark:bg-primary-dark/50 p-3 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50 shadow-sm">
                                <div className="flex items-center gap-2 mb-1 text-text-light/70 dark:text-text-dark/70">
                                  <FiCalendar className="w-4 h-4 text-accent-light dark:text-accent-dark" />
                                  <span className="text-xs font-medium uppercase tracking-wide">{STATS.TRAINING_PERIOD}</span>
                                </div>
                                <div className="text-sm">
                                  <div className="font-bold text-text-light dark:text-text-dark">
                                    {summary.trainingPeriod} days
                                  </div>
                                  <div className="text-xs mt-1 text-text-light/80 dark:text-text-dark/80">
                                    {format(new Date(summary.earliestDate), 'MMM d, yyyy')} - {format(new Date(summary.latestDate), 'MMM d, yyyy')}
                                  </div>
                                </div>
                              </div>
                              
                              {/* DORs Written Card */}
                              <div className="bg-primary-light/50 dark:bg-primary-dark/50 p-3 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50 shadow-sm">
                                <div className="flex items-center gap-2 mb-1 text-text-light/70 dark:text-text-dark/70">
                                  <FiFileText className="w-4 h-4 text-accent-light dark:text-accent-dark" />
                                  <span className="text-xs font-medium uppercase tracking-wide">{STATS.DORS_WRITTEN}</span>
                                </div>
                                <div className="font-bold text-sm text-text-light dark:text-text-dark flex items-baseline">
                                  {summary.daysTrained}
                                  <span className="text-xs ml-1 opacity-70 font-normal">reports</span>
                                </div>
                                {summary.eowrs.length > 0 && (
                                  <div className="text-xs mt-1 text-text-light/80 dark:text-text-dark/80">
                                    + {summary.eowrs.length} weekly reports
                                  </div>
                                )}
                              </div>
                              
                              {/* Total Hours Card */}
                              <div className="bg-primary-light/50 dark:bg-primary-dark/50 p-3 rounded-lg border border-secondary-light/50 dark:border-secondary-dark/50 shadow-sm">
                                <div className="flex items-center gap-2 mb-1 text-text-light/70 dark:text-text-dark/70">
                                  <FiClock className="w-4 h-4 text-accent-light dark:text-accent-dark" />
                                  <span className="text-xs font-medium uppercase tracking-wide">{STATS.TOTAL_HOURS}</span>
                                </div>
                                <div className="font-bold text-sm text-text-light dark:text-text-dark flex items-baseline">
                                  {summary.totalHours}
                                  <span className="text-xs ml-1 opacity-70 font-normal">hrs logged</span>
                                </div>
                                <div className="text-xs mt-1 text-text-light/80 dark:text-text-dark/80">
                                  Average: {summary.daysTrained > 0 ? (summary.totalHours / summary.daysTrained).toFixed(1) : 0} hrs per report
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Actions Tab */}
                          {getActiveTab(traineeId) === 'actions' && (
                            <div className="grid grid-cols-1 gap-3">
                              <ActionButton icon={FiFileText} text={BUTTONS.VIEW_REPORTS} onClick={() => navigate(`/trainee-reports/${traineeId}`)} disabled={pdfLoading || compilingTraineeId !== null}/>
                              <ActionButton icon={FiBook} text={BUTTONS.COVER_PAGE} onClick={() => handlePDFAction('cover', traineeId)} disabled={pdfLoading || compilingTraineeId !== null} isLoading={pdfLoading} />
                              <ActionButton icon={FiCheckCircle} text={BUTTONS.COMPLETION_MEMO} onClick={() => handlePDFAction('memo', traineeId)} disabled={pdfLoading || compilingTraineeId !== null} isLoading={pdfLoading} />
                              <ActionButton
                                icon={FiPackage}
                                text={BUTTONS.COMPILE_REPORTS}
                                onClick={() => handleCompileReports(traineeId)}
                                isLoading={compilingTraineeId === traineeId}
                                disabled={pdfLoading || (compilingTraineeId !== null && compilingTraineeId !== traineeId)}
                              />
                            </div>
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              ))}
              </motion.div>
            )}
          </AnimatePresence>
        <ConfirmationModal
          ref={modalRef}
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
        />
        </motion.div>
      </motion.div>
  );
};

export default DORsPage;